import styled from '@emotion/styled';
import { Alert, Box, Collapse, Stack, Tab, Tabs } from '@mui/material';
import React from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import { Paper } from '../../components/Paper';
import { StandardsLibraryProvider } from '../../contexts/StandardsLibraryContext';
import { useLocale } from '../../providers/LocaleProvider';
import CancelIcon from '../../resources/images/icons-close.svg';
import InfoIcon from '../../resources/images/icons-info-circle.svg';
import { KLAYO_COLORS } from '../../themes';
import SettingStandardsLibraryCompetencies from './SettingStandardsLibraryCompetencies';
import SettingStandardsLibraryJobs from './SettingStandardsLibraryJobs';
import SettingStandardsLibraryTasks from './SettingStandardsLibraryTasks';

const SettingStandardsLibraryView = (props: any) => {
  const { theme, organization } = props;
  const location = useLocation();
  const { translate } = useLocale();
  const [open, setOpen] = React.useState(true);

  // Base path for standards library
  const basePath = '/settings/standards-library';

  const isTrial = organization?.isTrial || !organization?.enabledFeatures?.KlayoIntelligence;

  return (
    <StandardsLibraryProvider>
      <StyledSettingStandardsLibraryView>
        <div className='klayo__setting-standards-library-desktop'>
          <Paper
            theme={theme}
            padding={{ xs: '46px 24px', md: '56px 64px' }}
            borderFromBreakpoint='md'
          >
            <StyledTitle>{translate('settingsViews.standardsLibrary.title')}</StyledTitle>
            <Collapse in={open}>
              <Alert
                icon={<InfoIcon />}
                severity='info'
                action={
                  <CancelIcon
                    onClick={() => {
                      setOpen(false);
                    }}
                    className='klayo__alert-close-icon'
                    style={{ cursor: 'pointer', padding: '0 0.575rem 0 0' }}
                  />
                }
                onClose={() => {}}
                sx={{
                  alignItems: 'center',
                  fontWeight: 500,
                  borderRadius: '2rem',
                  color: KLAYO_COLORS.GreyGR900,
                  fontSize: '.875rem',
                }}
              >
                {translate('settingsViews.standardsLibrary.infoMessage')}{' '}
                {isTrial && (
                  <>
                    <br />
                    <b>
                      {translate('settingsViews.standardsLibrary.contactSupport')}{' '}
                      <a href='mailto:support@klayo.com'> support@klayo.com </a>{' '}
                      {translate('settingsViews.standardsLibrary.toUpgrade')}
                    </b>
                  </>
                )}
              </Alert>
            </Collapse>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                centered={true}
                value={location.pathname}
                variant='fullWidth'
                sx={{ width: '100%' }}
              >
                <Tab
                  label={translate('settingsViews.standardsLibrary.jobs')}
                  to={basePath}
                  component={Link}
                  value={basePath}
                />
                <Tab
                  label={translate('settingsViews.standardsLibrary.tasks')}
                  to={`${basePath}/tasks`}
                  component={Link}
                  value={`${basePath}/tasks`}
                />
                <Tab
                  label={translate('settingsViews.standardsLibrary.competencies')}
                  to={`${basePath}/competencies`}
                  component={Link}
                  value={`${basePath}/competencies`}
                />
              </Tabs>
            </Box>
            <StyledSection>
              <Routes>
                <Route index element={<SettingStandardsLibraryJobs {...props} />} />
                <Route path='tasks' element={<SettingStandardsLibraryTasks {...props} />} />
                <Route
                  path='competencies'
                  element={<SettingStandardsLibraryCompetencies {...props} />}
                />
              </Routes>
            </StyledSection>
          </Paper>
        </div>
      </StyledSettingStandardsLibraryView>
    </StandardsLibraryProvider>
  );
};

const StyledSettingStandardsLibraryView = styled.div`
  .klayo__setting-standards-library-mobile {
    display: none;
  }

  .MuiAlert-action {
    path[fill='#111827'] {
      fill: ${KLAYO_COLORS.NeonBlueNB700};
    }
  }

  @media (max-width: 600px) {
    .klayo__setting-standards-library-desktop {
      display: none;
    }

    .klayo__setting-standards-library-mobile {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 80vh;

      .content {
        color: ${KLAYO_COLORS.RedWarning};
        font-family: Inter;
        font-size: 20px;
        font-style: normal;
        font-weight: 400;
        line-height: 28px;
        letter-spacing: -0.3px;
        text-align: center;
      }
    }
  }
`;

const StyledTitle = styled.h1`
  @media (max-width: 600px) {
    margin-bottom: 32px !important;
  }
`;

const StyledSection = styled(Stack)`
  padding: 40px 0px 24px 0px;

  @media (max-width: 600px) {
    padding: 32px 0px 24px 0px;
  }
`;

export default SettingStandardsLibraryView;
